import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAPI } from '../../hooks/useAPI';
import { getTalents, getSpecialties, getSkills, getLanguages } from '../../services/api/talents';
import Loading from '../../components/ui/Loading';
import ErrorMessage from '../../components/ui/ErrorMessage';
import Pagination from '../../components/ui/Pagination';
import SpecialtyNav from '../../components/business/SpecialtyNav';
import TalentFilter from '../../components/business/TalentFilter';
import TalentCard from '../../components/business/TalentCard';

const TalentsPage = () => {
  const { t, i18n } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [activeSpecialty, setActiveSpecialty] = useState('all');
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  // 获取人才列表
  const { 
    data: talentsResponse, 
    loading: talentsLoading, 
    error: talentsError,
    refetch: refetchTalents 
  } = useAPI(() => getTalents({
    page: currentPage,
    pageSize: 12
  }), [currentPage, i18n.language]);

  // 处理API返回的数据结构
  // 添加日志输出，查看API返回的数据
  console.log('Talents API response:', talentsResponse);
  
  // 尝试不同的数据结构路径
  const talents = talentsResponse?.records || talentsResponse?.talents || [];
  console.log('Processed talents:', talents);
  
  // 确保分页数据正确
  const totalCount = talentsResponse?.total || talents.length || 0;
  const apiCurrentPage = talentsResponse?.current || 1;
  const totalPages = talentsResponse?.pages || Math.ceil(totalCount / 12) || 1;
  
  const pagination = {
    totalCount,
    currentPage: apiCurrentPage,
    totalPages
  };
  console.log('Pagination data:', pagination);

  // 将所有人才数据存储到sessionStorage中
  useEffect(() => {
    if (talents.length > 0) {
      sessionStorage.setItem('allTalents', JSON.stringify(talents));
    }
  }, [talents]);

  // 处理专业领域切换
  const handleSpecialtyChange = (specialty) => {
    setActiveSpecialty(specialty);
    setSelectedSkills([]); // 切换专业领域时清空技能选择
    setCurrentPage(1); // 重置页码
  };

  // 处理技能筛选
  const handleSkillsChange = (skills) => {
    setSelectedSkills(skills);
    setCurrentPage(1);
  };

  // 处理语言筛选
  const handleLanguagesChange = (languages) => {
    setSelectedLanguages(languages);
    setCurrentPage(1);
  };

  // 清除所有筛选
  const handleClearFilters = () => {
    setSelectedSkills([]);
    setSelectedLanguages([]);
    setCurrentPage(1);
  };

  // 处理搜索
  const handleSearch = (keyword) => {
    setSearchKeyword(keyword);
    setCurrentPage(1);
  };

  // 处理排序
  const handleSortChange = (field, order) => {
    setSortBy(field);
    setSortOrder(order);
    setCurrentPage(1);
  };

  // 处理联系人才
  


  const isLoading = talentsLoading;

  // 人才卡片骨架屏
  const TalentSkeleton = () => (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden animate-pulse">
      <div className="aspect-w-1 aspect-h-1 bg-gray-200 h-48"></div>
      <div className="p-4 space-y-3">
        <div className="h-5 bg-gray-200 rounded w-3/4"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        <div className="h-4 bg-gray-200 rounded w-2/3"></div>
      </div>
    </div>
  )

  return (
      <div className="min-h-screen bg-gray-50">
        {/* Hero Banner */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl font-bold mb-4">{t('talents.title')}</h1>
            <p className="text-xl mb-8 text-primary-100">
              {t('talents.subtitle')}
            </p>
            
            {/* 搜索框 */}
            {/* <div className="max-w-md mx-auto">
              <div className="flex">
                <input
                  type="text"
                  placeholder={t('talents.searchPlaceholder')}
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch(e.target.value);
                    }
                  }}
                  className="flex-1 px-4 py-3 rounded-l-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
                <button
                  onClick={() => handleSearch(searchKeyword)}
                  className="px-6 py-3 bg-primary-500 hover:bg-primary-400 rounded-r-lg transition-colors duration-200"
                >
                  🔍
                </button>
              </div>
            </div> */}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

          {/* 专业领域导航
          <SpecialtyNav
            specialties={specialties || []}
            activeSpecialty={activeSpecialty}
            onSpecialtyChange={handleSpecialtyChange}
          /> */}

          {/* 筛选条件 */}
          {/* <TalentFilter
            skills={skills || []}
            languages={languages || []}
            selectedSkills={selectedSkills}
            selectedLanguages={selectedLanguages}
            onSkillsChange={handleSkillsChange}
            onLanguagesChange={handleLanguagesChange}
            onClearFilters={handleClearFilters}
            currentSpecialty={activeSpecialty}
          /> */}

          {/* 结果统计和排序 */}
          <div className="flex justify-between items-center mb-6">
            <div className="text-sm text-gray-600">
              {pagination.totalCount > 0 && (
                <>
                  {t('talents.results.found', { count: pagination.totalCount })}
                  {activeSpecialty !== 'all' && (
                    <span className="ml-2 text-primary-600">
                      ({t('talents.results.currentSpecialty', { 
                        specialty: t(`talents.specialties.${activeSpecialty}`)
                      })})
                    </span>
                  )}
                </>
              )}
            </div>

            {/* 排序选择 */}
            {/* <div className="flex items-center space-x-4">
              <select
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value, sortOrder)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="name">{t('talents.sort.name')}</option>
                <option value="experience">{t('talents.sort.experience')}</option>
                <option value="popularity">{t('talents.sort.popularity')}</option>
                <option value="rating">{t('talents.sort.rating')}</option>
              </select>
              <button
                onClick={() => handleSortChange(sortBy, sortOrder === 'asc' ? 'desc' : 'asc')}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {sortOrder === 'asc' ? '↑' : '↓'} {t(`talents.sort.${sortOrder}`)}
              </button>
            </div> */}
          </div>

          {/* 错误状态 */}
          {talentsError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <ErrorMessage message={talentsError} onRetry={refetchTalents} />
            </div>
          )}

          {/* 人才列表 */}
          {isLoading ? (
            // 加载中显示骨架屏
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
              {[...Array(8)].map((_, i) => (
                <TalentSkeleton key={i} />
              ))}
            </div>
          ) : talentsError ? (
            <div></div>
          ) : talents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
              {talents.map((talent) => (
                <TalentCard
                  key={talent.talentId}
                  talent={talent}
                />
              ))}
            </div>
          ) : (
            <div className="col-span-full text-center py-16">
              <h3 className="text-4xl font-medium text-gray-900 mb-2">{t('common.comingSoon.title')}</h3>
              <p className="text-gray-600">{t('common.comingSoon.subtitle')}</p>
            </div>
          )}

          {/* 分页 */}
          <div className="flex justify-center mt-8">
            <Pagination
              currentPage={apiCurrentPage}
              totalPages={totalPages}
              totalItems={totalCount}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      </div>
  );
};

export default TalentsPage; 